// Nibiru — the secret 12th planet. A fully procedural gas giant
// rendered without textures, drifting on a highly inclined and
// eccentric orbit far beyond the canonical solar system.
//
// Lore reference: Zecharia Sitchin's "The 12th Planet" (1976), where
// Nibiru is described as a reddish wandering world on a 3,600-year
// orbit. Modern doomsday lore took the idea and ran. Here it's an
// easter egg: not on any UI list, not in autopilot, not in the planet
// nav. Discoverable via the URL hash `#nibiru`, the window function
// `window.__nibiru.goto()`, or by accident if you fly far enough out
// past Pluto.
//
// Visual identity:
//   - Banded gas giant structure like Jupiter (so it reads as a
//     plausible planet, not abstract noise)
//   - Wine / sienna / cream palette with a deep crimson great vortex
//     so it's distinctly NOT Jupiter at a glance
//   - 47° tilt for the alien-intruder feel (Jupiter is 3°)
//   - Slight retrograde rotation, matching Sitchin's claim of an
//     opposing orbital direction
//   - Atmospheric rim glow tinted red so the limb signs the lore

import * as THREE from 'three';
import {
  proceduralGasGiantVertexShader,
  proceduralGasGiantFragmentShader
} from '../shaders/proceduralGasGiant.js';

// Placement. Sitchin's 3,600-year orbit has a semi-major axis of
// 3600^(2/3) = 235 AU by Kepler's third law, so Nibiru sits 235 AU
// from the Sun, about six times Pluto's mean distance (39.5 AU), 12
// degrees above the ecliptic like a scattered body. It was a fixed
// scene position (6,800 units), which at true scale (5,000 units an
// AU) put it at 1.4 AU, between the Earth and Mars, and in artistic
// mode fell inside Pluto's orbit once the spread passed about 8x. It
// is placed every frame from the scale the planets are drawn at: true
// AU in realistic mode, and in artistic mode the same multiple of
// Pluto's drawn distance. URL hash and autopilot still teleport
// directly, so the distance is only a visual penalty, not an access one.
export const NIBIRU_AU = 235;
const PLUTO_AU = 39.48;
const NIBIRU_DIRECTION = new THREE.Vector3(5500, 1400, -3800).normalize();

export function createNibiru() {
  const group = new THREE.Group();
  group.name = 'Nibiru';
  // Hint to any future systems that might scrape the scene graph: this
  // body is hidden from UI lists, autopilot, and the planet picker.
  group.userData = {
    isPlanet: false, // Don't appear in planet-iteration code paths
    isSecret: true,
    bodyId: 'nibiru',
    displayName: 'Nibiru',
    description: 'A wandering twelfth world on a 3,600-year orbit. Procedurally rendered, no textures.',
    // Visual radius used by the autopilot to pick an approach
    // distance via fastTravelApproachDistance(target, radius).
    // Without this the autopilot falls back to radius=1 and arrives
    // inside the body. 7.5 matches the mesh geometry.
    radius: 7.5
  };

  // Geometry: high segment count so the procedural noise has enough
  // surface samples to resolve fine band features even at extreme
  // zoom. The shader does the heavy detail work per fragment, but a
  // low-poly sphere shows polygonal silhouette at the limb.
  const radius = 7.5;
  const geometry = new THREE.SphereGeometry(radius, 128, 96);

  const uniforms = {
    uTime: { value: 0 },
    uSunDir: { value: new THREE.Vector3(-1, 0, 0) },
    // Wine / blood-iron palette. Stops chosen so the OKLCh mix
    // produces:
    //   deep   → dark wine/burgundy (the storm-shadow side)
    //   belt   → burnt sienna (the warm "iron oxide" midtone)
    //   zone   → pale wheat/cream with a red lift (the bright bands)
    //   storm  → crimson vortex (the great spot)
    uPaletteDeep:  { value: new THREE.Color(0.16, 0.045, 0.07) },
    uPaletteBelt:  { value: new THREE.Color(0.74, 0.30, 0.16) },
    uPaletteZone:  { value: new THREE.Color(0.94, 0.78, 0.62) },
    uPaletteStorm: { value: new THREE.Color(0.62, 0.10, 0.13) },
    // 24 sin cycles pole-to-pole gives roughly twelve dark belts and
    // twelve light zones, matching Jupiter's level of visible
    // banding density.
    uBandCount:   { value: 24.0 },
    // Higher than the canonical Quilez warp amount because we want
    // the festoons to look stormy, not Jupiter-tidy.
    uTurbulence:  { value: 1.75 },
    // Storm disabled — the circular SDF was reading as a literal eye
    // on the disc rather than a natural atmospheric feature. With
    // size pinned to a tiny value the storm mask never activates, so
    // the surface is band + FBM detail only, which reads as a real
    // gas-giant atmosphere instead of a Cyclopean stare.
    uStormLatDeg: { value: -18.0 },
    uStormLonDeg: { value: 105.0 },
    uStormSize:   { value: 0.001 },
    uLimbDarken:  { value: 0.85 },
    uRimGlow:     { value: 0.55 },
    // Cloud-detail boost — Nibiru gets the maximum detail strength
    // and a high feature frequency so the per-fragment FBM resolves
    // into the multi-scale "weather" overlay that gives it that
    // turbulent Jupiter-class atmosphere read. Default = 1.0 / 7.0
    // (subtle), Nibiru = 2.2 / 9.0 (rich).
    uCloudDetailStrength: { value: 2.2 },
    uCloudDetailFreq:     { value: 9.0 },
    // Full chaos — meandering bands, variable widths, per-band
    // brightness variation. Breaks the "too mathematical" perfect-
    // periodicity look so Nibiru reads as a real atmospheric body.
    uBandChaos:           { value: 1.0 }
  };

  const material = new THREE.ShaderMaterial({
    uniforms,
    vertexShader: proceduralGasGiantVertexShader,
    fragmentShader: proceduralGasGiantFragmentShader,
    side: THREE.FrontSide,
    transparent: false
  });

  const mesh = new THREE.Mesh(geometry, material);
  mesh.name = 'NibiruMesh';
  group.add(mesh);

  // 47° axial tilt — half-way between Uranus' near-toppled 97° and
  // Earth's 23°. Reads as "this thing was knocked over by something"
  // which fits the rogue-planet vibe. Tilt the GROUP so the planet's
  // local Y axis (its spin axis in the shader) tips relative to the
  // ecliptic — the shader latitude bands then align with the tilted
  // axis as you'd expect from a real off-axis world.
  group.rotation.z = THREE.MathUtils.degToRad(47);

  group.position.copy(NIBIRU_DIRECTION).multiplyScalar(NIBIRU_AU * 5000);

  // Sun direction scratch — reused per frame so tick() doesn't
  // allocate.
  const _sunDir = new THREE.Vector3();
  const _bodyWorld = new THREE.Vector3();

  return {
    group,
    mesh,
    uniforms,
    /**
     * Per-frame update: advance time, spin the body, and refresh the
     * sun direction so the lit hemisphere tracks the real Sol.
     *
     * @param {number} delta seconds since last frame
     * @param {number} elapsed total elapsed seconds since clock start
     * @param {THREE.Object3D|null} sunObject the scene's sun group, or null
     * @param {object} scale { auToScene } in realistic mode (scene units
     *   per AU), or { plutoDistance } in artistic mode (Pluto's drawn
     *   distance from the Sun in scene units)
     */
    tick(delta, elapsed, sunObject, scale = {}) {
      const perAu = scale.auToScene || (scale.plutoDistance ? scale.plutoDistance / PLUTO_AU : 0);
      if (perAu > 0) group.position.copy(NIBIRU_DIRECTION).multiplyScalar(NIBIRU_AU * perAu);

      // Wind speed dialled down to 10% of baseline (was 30%). All of
      // the shader's drift + warp + cloud-detail offsets key off
      // uTime, so a single scalar slows every motion uniformly.
      // Reads as a heavy, deliberately-moving atmosphere — the
      // storms and bands shift just slowly enough that you can see
      // them moving but it feels menacing, not hectic.
      uniforms.uTime.value = elapsed * 0.10;

      // Retrograde-ish slow spin. Negative for the lore-flavour
      // reverse rotation; magnitude pulled down to 0.025 rad/sec so
      // a full rotation takes about four minutes at default time
      // speed — visibly turning but heavy, menacing, the way a real
      // ten-Earth-mass body should look from orbit.
      mesh.rotation.y -= delta * 0.025;

      // Refresh sun direction. The shader expects a unit vector
      // pointing from the body toward the sun in world space.
      if (sunObject) {
        sunObject.getWorldPosition(_sunDir);
        group.getWorldPosition(_bodyWorld);
        _sunDir.sub(_bodyWorld);
        if (_sunDir.lengthSq() > 1e-6) {
          _sunDir.normalize();
          uniforms.uSunDir.value.copy(_sunDir);
        }
      }
    },
    dispose() {
      geometry.dispose();
      material.dispose();
      if (group.parent) group.parent.remove(group);
    }
  };
}
